import { fetchImage } from "../services/imageReadService";

/**
 * Caption geometry uses image *width* so font size stays the same across
 * square (Warhol) and taller (cartoon/caricature) outputs.
 * Edge inset is the same px for top and bottom placements.
 */
const FONT_SIZE_RATIO = 0.048;
const MIN_FONT_SIZE_RATIO = 0.032;
const SIDE_PADDING_RATIO = 0.06;
/**
 * Edge gap as a fraction of width. Kept modest so bottom captions (cartoon /
 * caricature / others) sit a bit lower and Warhol top sits a bit higher.
 */
const EDGE_PADDING_RATIO = 0.028;
const LINE_HEIGHT_RATIO = 1.2;
const MAX_LINES = 3;
const DEFAULT_FONT_FAMILY = "Inter, system-ui, sans-serif";

export type UserLinePosition = "top" | "bottom";

function loadImageElement(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Generated image failed to load"));
        img.src = src;
    });
}

/**
 * Loads through the image-read proxy for remote URLs so the canvas stays
 * untainted and `toDataURL` keeps working.
 */
async function loadDrawableImage(url: string): Promise<{ image: HTMLImageElement; revoke?: () => void }> {
    if (url.startsWith("data:") || url.startsWith("blob:")) {
        return { image: await loadImageElement(url) };
    }

    const response = await fetchImage(url);
    if (!response.ok) {
        throw new Error(`Generated image fetch failed: ${response.status}`);
    }
    const objectUrl = URL.createObjectURL(await response.blob());
    try {
        const image = await loadImageElement(objectUrl);
        return { image, revoke: () => URL.revokeObjectURL(objectUrl) };
    } catch (error) {
        URL.revokeObjectURL(objectUrl);
        throw error;
    }
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = "";

    for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxWidth || !current) {
            current = candidate;
        } else {
            lines.push(current);
            current = word;
        }
    }
    if (current) lines.push(current);
    return lines;
}

function canvasFont(fontSize: number, fontFamily: string): string {
    return `700 ${fontSize}px ${fontFamily}`;
}

async function ensureFontLoaded(fontFamily: string, fontSize: number): Promise<void> {
    if (!document.fonts?.load) return;
    const primary = fontFamily.split(",")[0]?.trim().replace(/^["']|["']$/g, "") || "Inter";
    try {
        await document.fonts.load(`700 ${fontSize}px "${primary}"`);
        await document.fonts.load(`400 ${fontSize}px "${primary}"`);
    } catch {
        /* fall back to whatever is available */
    }
}

/**
 * Shrinks the font until the caption fits the allowed width in at most
 * `MAX_LINES` lines. Returns the chosen size plus the wrapped lines.
 */
function fitCaption(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    startFontSize: number,
    minFontSize: number,
    fontFamily: string
): { fontSize: number; lines: string[] } {
    let fontSize = startFontSize;
    let lines: string[] = [];

    while (fontSize > minFontSize) {
        ctx.font = canvasFont(fontSize, fontFamily);
        lines = wrapLines(ctx, text, maxWidth);
        if (lines.length <= MAX_LINES) {
            return { fontSize, lines };
        }
        fontSize -= 1;
    }

    ctx.font = canvasFont(minFontSize, fontFamily);
    lines = wrapLines(ctx, text, maxWidth).slice(0, MAX_LINES);
    return { fontSize: minFontSize, lines };
}

/**
 * Renders the user's optional line as a caption on the generated artwork.
 * The line is never sent to the generation API, so the text is composited
 * locally and returned as a PNG data URL. Any failure resolves to the
 * original URL so the kiosk still shows the artwork.
 *
 * Default position is bottom; pass `position: "top"` for styles that need
 * the caption above the subject (e.g. Andy Warhol Marilyn Monroe).
 */
export async function drawUserLineOnImage(
    imageUrl: string,
    userLine: string,
    options?: { position?: UserLinePosition; fontFamily?: string }
): Promise<string> {
    const url = (imageUrl || "").trim();
    const text = (userLine || "").trim().replace(/\s+/g, " ");
    const position: UserLinePosition = options?.position === "top" ? "top" : "bottom";
    const fontFamily = (options?.fontFamily || "").trim() || DEFAULT_FONT_FAMILY;
    if (!url || !text) return url;

    let revoke: (() => void) | undefined;
    try {
        if (document.fonts?.ready) {
            await document.fonts.ready;
        }

        const drawable = await loadDrawableImage(url);
        revoke = drawable.revoke;
        const { image } = drawable;

        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) return url;

        const startFontSize = Math.round(width * FONT_SIZE_RATIO);
        await ensureFontLoaded(fontFamily, startFontSize);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return url;

        ctx.drawImage(image, 0, 0, width, height);

        const maxTextWidth = width * (1 - SIDE_PADDING_RATIO * 2);
        // Width-based size so cartoon/caricature and Warhol share the same px size.
        const { fontSize, lines } = fitCaption(
            ctx,
            text,
            maxTextWidth,
            startFontSize,
            Math.round(width * MIN_FONT_SIZE_RATIO),
            fontFamily
        );
        if (!lines.length) return url;

        const lineHeight = fontSize * LINE_HEIGHT_RATIO;
        // Same edge inset (px) for Warhol top and cartoon/caricature bottom.
        const edgePadPx = Math.round(width * EDGE_PADDING_RATIO);

        ctx.font = canvasFont(fontSize, fontFamily);
        ctx.textAlign = "center";
        ctx.textBaseline = position === "top" ? "top" : "bottom";
        ctx.lineJoin = "round";
        ctx.shadowColor = "rgba(0, 0, 0, 0.45)";
        ctx.shadowBlur = fontSize * 0.22;
        ctx.shadowOffsetY = fontSize * 0.06;
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = Math.max(2, fontSize * 0.16);
        ctx.fillStyle = "#ffffff";

        lines.forEach((line, index) => {
            const drawY =
                position === "top"
                    ? edgePadPx + index * lineHeight
                    : height - edgePadPx - (lines.length - 1 - index) * lineHeight;
            ctx.strokeText(line, width / 2, drawY);
            ctx.fillText(line, width / 2, drawY);
        });

        return canvas.toDataURL("image/png");
    } catch (error) {
        console.error("drawUserLineOnImage:", error);
        return url;
    } finally {
        revoke?.();
    }
}
